/**
 * AccountFilterPopover Component
 * 
 * Account filter button and popover with grouped account selection.
 * Uses FloatingLabelFilterButton for consistent UI with Activity page.
 */

import React from "react";
import { Grid } from "@mui/material";
import { Account } from "../../../types";
import FloatingLabelFilterButton, { FilterOption } from "../../../components/FloatingLabelFilterButton";

export interface AccountFilterPopoverProps {
  accounts: Account[];
  selectedAccounts: string[];
  onAccountToggle: (accountId: string) => void;
  onSelectAllAccounts: () => void;
  onFilterClick: (event: React.MouseEvent<HTMLElement>) => void;
  onFilterClose: () => void;
  anchorEl: HTMLElement | null;
  getAccountDisplayName: (accountId: string) => string;
}

/**
 * Account filter button and popover
 */
export const AccountFilterPopover: React.FC<AccountFilterPopoverProps> = ({
  accounts,
  selectedAccounts,
  onAccountToggle,
  onSelectAllAccounts,
  onFilterClick,
  onFilterClose,
  anchorEl,
  getAccountDisplayName,
}) => {
  if (accounts.length === 0) {
    return null;
  }

  // Convert accounts to FilterOption format
  const accountOptions: FilterOption[] = accounts.map(account => ({
    id: account.id,
    label: account.accountName,
    group: account.group,
  }));

  // Handle selection change - efficiently sync selection state
  const handleSelectionChange = (selectedIds: string[]): void => {
    const currentSet = new Set(selectedAccounts);
    const newSet = new Set(selectedIds);
    
    // If selection matches "all" or "none", use the select all handler
    if (selectedIds.length === 0 || selectedIds.length === accounts.length) {
      // Check if current state matches - if not, toggle to match
      const allSelected = selectedAccounts.length === accounts.length;
      const noneSelected = selectedAccounts.length === 0;
      const shouldBeAll = selectedIds.length === accounts.length;
      
      if ((shouldBeAll && !allSelected) || (!shouldBeAll && !noneSelected)) {
        onSelectAllAccounts();
      }
      return;
    }
    
    // Otherwise, toggle accounts that differ
    // Add missing accounts
    selectedIds.forEach(id => {
      if (!currentSet.has(id)) {
        onAccountToggle(id);
      }
    });
    
    // Remove accounts that shouldn't be selected
    selectedAccounts.forEach(id => {
      if (!newSet.has(id)) {
        onAccountToggle(id);
      }
    });
  };

  return (
    <>
      {/* @ts-expect-error - MUI Grid item prop types issue in v7 */}
      <Grid item xs={12} sm={6} md={4}>
        <FloatingLabelFilterButton
          label="Account"
          options={accountOptions}
          selectedIds={selectedAccounts}
          onSelectionChange={handleSelectionChange}
          getDisplayName={getAccountDisplayName}
          allSelectedText="All Accounts"
          multipleSelectedText={(count: number) => `${count} Accounts`}
          anchorEl={anchorEl}
          onOpen={onFilterClick}
          onClose={onFilterClose}
          groupOptions={true}
          groupOrder={["Cash", "Accounts", "Cards", "Overdrafts", "Debts"]}
          minWidth={130}
        />
      </Grid>
    </>
  );
};
